import type { ChatFileAttachment } from "@v2/features/chat/utils/chatFileAttachment";

import React, { useMemo } from "react";

import { VideoThumbnail } from "@agent-ui/features/chat/components/chat-elements/VideoThumbnail";
import { FileIcon, X } from "lucide-react";
import { motion } from "motion/react";

interface FilePreviewProps {
  file: ChatFileAttachment;
  onRemove?: () => void;
}

interface RemoveButtonProps {
  onRemove: () => void;
  onClick?: (e: React.MouseEvent) => void;
}

const RemoveButton = ({ onRemove, onClick }: RemoveButtonProps) => (
  <button
    className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-white"
    type="button"
    onClick={onClick ?? onRemove}
    aria-label="Remove attachment"
  >
    <X className="h-2.5 w-2.5" />
  </button>
);

export const FilePreview = (props: FilePreviewProps) => {
  const rawFile = props.file.file;

  if (rawFile.type.startsWith("image/")) {
    return <ImageFilePreview {...props} />;
  }

  if (rawFile.type.startsWith("video/")) {
    return <VideoFilePreview {...props} />;
  }

  if (
    rawFile.type.startsWith("text/") ||
    rawFile.name.endsWith(".txt") ||
    rawFile.name.endsWith(".md")
  ) {
    return <TextFilePreview {...props} />;
  }

  return <GenericFilePreview {...props} />;
};

const ImageFilePreview = ({ file, onRemove }: FilePreviewProps) => {
  return (
    <motion.div
      className="relative flex max-w-[300px] rounded-md text-2xs"
      layout
      initial={{ opacity: 0, y: "100%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "100%" }}
    >
      <img
        alt={`Attachment ${file.file.name}`}
        className="grid h-14 w-14 shrink-0 place-items-center rounded-sm border border-border bg-muted object-cover"
        src={URL.createObjectURL(file.file)}
      />

      {onRemove ? <RemoveButton onRemove={onRemove} /> : null}
    </motion.div>
  );
};

const VideoFilePreview = ({ file, onRemove }: FilePreviewProps) => {
  const videoSrc = useMemo(() => URL.createObjectURL(file.file), [file.file]);

  return (
    <motion.div
      className="relative flex max-w-[300px] rounded-md text-2xs"
      layout
      initial={{ opacity: 0, y: "100%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "100%" }}
    >
      <VideoThumbnail src={videoSrc} size="small" />

      {onRemove ? (
        <RemoveButton
          onRemove={onRemove}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        />
      ) : null}
    </motion.div>
  );
};

const TextFilePreview = ({ file, onRemove }: FilePreviewProps) => {
  return (
    <motion.div
      className="relative flex max-w-[200px] rounded-md border p-1.5 pr-2 text-2xs"
      layout
      initial={{ opacity: 0, y: "100%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "100%" }}
    >
      <div className="flex w-full items-center space-x-2">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-sm border bg-muted p-0.5">
          <div className="h-full w-full overflow-hidden text-[6px] leading-none text-muted-foreground">
            {file.textPreview ?? ""}
          </div>
        </div>
      </div>

      {onRemove ? (
        <button
          className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full border bg-background"
          type="button"
          onClick={onRemove}
          aria-label="Remove attachment"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      ) : null}
    </motion.div>
  );
};

const GenericFilePreview = ({ onRemove }: FilePreviewProps) => {
  return (
    <motion.div
      className="relative flex max-w-[200px] rounded-md border p-1.5 pr-2 text-2xs"
      layout
      initial={{ opacity: 0, y: "100%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "100%" }}
    >
      <div className="flex w-full items-center space-x-2">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-sm border bg-muted">
          <FileIcon className="h-6 w-6 text-foreground" />
        </div>
      </div>

      {onRemove ? (
        <button
          className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full border bg-background"
          type="button"
          onClick={onRemove}
          aria-label="Remove attachment"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      ) : null}
    </motion.div>
  );
};
